import { ReactNode, useState } from "react";
import { ArrowRight, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { agentGuideImageSrc, type AgentStarterPrompt, type PartialAgent } from "~/services/agents";
import { StarterPrompts } from "./StarterPrompts";

const guideCardCollapsedStorageKey = (agentId: string) => `guide-card-collapsed:${agentId}`;

const readGuideCardCollapsed = (agentId: string): boolean => {
  try {
    return localStorage.getItem(guideCardCollapsedStorageKey(agentId)) === "true";
  } catch {
    return false;
  }
};

const writeGuideCardCollapsed = (agentId: string, collapsed: boolean) => {
  try {
    localStorage.setItem(guideCardCollapsedStorageKey(agentId), String(collapsed));
  } catch {
    // Storage unavailable (e.g. private browsing) — the collapse state just won't persist.
  }
};

interface GuideCardHeaderProps {
  agent: PartialAgent;
  avatar?: ReactNode;
  nameAction?: ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  // Hides the Collapse/Expand control entirely when there's nothing for it
  // to reveal — expanding would just show the same identity row again.
  showToggle: boolean;
}

// Identity row shared by both card states: avatar/name/switcher centered via
// a 3-column grid, with the Collapse/Expand control pinned to the right —
// the same row position in both states so toggling doesn't shift anything.
const GuideCardHeader = ({
  agent,
  avatar,
  nameAction,
  collapsed,
  onToggle,
  showToggle,
}: GuideCardHeaderProps) => (
  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-5 pt-5">
    <div />
    <div className="flex items-center justify-center gap-3">
      {avatar ?? <AgentAvatar agent={agent} size={48} />}
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-bold text-foreground">{agent.display_name}</h1>
        {nameAction}
      </div>
    </div>
    {showToggle && (
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggle}
        aria-label={collapsed ? "Expand" : "Collapse"}
        className="size-8 justify-self-end text-muted-foreground hover:text-foreground"
      >
        {collapsed ? (
          <Maximize2 className="size-4" aria-hidden="true" />
        ) : (
          <Minimize2 className="size-4" aria-hidden="true" />
        )}
      </Button>
    )}
  </div>
);

interface GuideCardProps {
  agent: PartialAgent;
  avatar?: ReactNode;
  nameAction?: ReactNode;
  headerExtras?: ReactNode;
  starterPrompts: AgentStarterPrompt[];
  onPromptClick: (text: string) => void;
  onOpenOverview?: () => void;
  collapsed: boolean;
  onToggleCollapsed: (collapsed: boolean) => void;
}

// Every agent gets this card — the agent's identity lives inside it in both
// states (toggled via the header's Collapse/Expand button, not a separate
// icon). Collapsed shows identity + starter-prompt chips; expanded shows
// guide_content (falling back to the plain description when unset), a
// Learn more link into the agent's Overview tab, an optional
// guide_image_url, and the starter prompts as a 2-column grid.
const GuideCard = ({
  agent,
  avatar,
  nameAction,
  headerExtras,
  starterPrompts,
  onPromptClick,
  onOpenOverview,
  collapsed,
  onToggleCollapsed,
}: GuideCardProps) => {
  const hasExpandedContent = Boolean(
    agent.guide_content || agent.description || agent.guide_image_url || starterPrompts.length > 0
  );

  return (
    <Card className="w-full max-w-2xl gap-4 p-0 pb-5">
      <GuideCardHeader
        agent={agent}
        avatar={avatar}
        nameAction={nameAction}
        collapsed={collapsed}
        onToggle={() => onToggleCollapsed(!collapsed)}
        showToggle={hasExpandedContent}
      />
      {headerExtras && <div className="flex justify-center px-5">{headerExtras}</div>}
      {collapsed
        ? starterPrompts.length > 0 && (
            <div className="px-5">
              <StarterPrompts
                variant="chips"
                prompts={starterPrompts}
                onPromptClick={onPromptClick}
              />
            </div>
          )
        : hasExpandedContent && (
            <div className="flex flex-col gap-4 px-5">
              {agent.guide_content ? (
                <MarkdownPreview>{agent.guide_content}</MarkdownPreview>
              ) : (
                // No guide_content — fall back to the agent's description so
                // the card isn't just a bare "Learn more" link.
                agent.description && <MarkdownPreview>{agent.description}</MarkdownPreview>
              )}
              {onOpenOverview && (
                <Button
                  variant="link"
                  onClick={onOpenOverview}
                  className="-mt-2 h-auto w-fit gap-1 p-0 text-sm font-medium has-[>svg]:px-0"
                >
                  Learn more
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Button>
              )}
              {agent.guide_image_url && (
                <div className="overflow-hidden rounded-lg border">
                  <img
                    src={agentGuideImageSrc(agent.id, agent.guide_image_url)}
                    alt={`${agent.display_name} guide`}
                    className="w-full"
                  />
                </div>
              )}
              {starterPrompts.length > 0 && (
                <div className="flex flex-col gap-3">
                  <p className="text-sm">
                    <span className="font-semibold text-foreground">Guided Workflows</span>{" "}
                    <span className="text-muted-foreground">- click to start</span>
                  </p>
                  <StarterPrompts
                    variant="grid"
                    prompts={starterPrompts}
                    onPromptClick={onPromptClick}
                  />
                </div>
              )}
            </div>
          )}
    </Card>
  );
};

interface EmptyConversationViewProps {
  // Agent whose avatar, name, and description anchor the view. Null while the
  // bound agent is still resolving.
  agent: PartialAgent | null;
  // Replaces the default 48px avatar. Defaults to a static AgentAvatar.
  avatar?: ReactNode;
  // Rendered inline to the right of the agent name — used by the New Session
  // splash for the agent-switcher button.
  nameAction?: ReactNode;
  // Rendered beneath the agent identity row, centered — used by the splash
  // for the dataset chip.
  headerExtras?: ReactNode;
  // Opens the session context sidebar to this agent's Overview tab, surfaced
  // as a "Learn more" link in the expanded guide card.
  onOpenOverview?: () => void;
  starterPrompts: AgentStarterPrompt[];
  onPromptClick: (text: string) => void;
  // Pin the avatar at a fixed offset from the top instead of vertically
  // centering the whole stack. Used by the New Session splash so swapping
  // agents (which changes the name/description/prompt height below) never
  // shifts the avatar. The live empty conversation has a fixed agent, so it
  // stays centered.
  anchorTop?: boolean;
  // The composer.
  children: ReactNode;
}

// Centered "empty conversation" layout: the agent's guide card and a
// composer. Shared by the live empty-conversation state and the New Session
// splash so the two stay visually identical.
export const EmptyConversationView = ({
  agent,
  avatar,
  nameAction,
  headerExtras,
  onOpenOverview,
  starterPrompts,
  onPromptClick,
  anchorTop = false,
  children,
}: EmptyConversationViewProps) => {
  // Switching the selected agent doesn't remount this view on the New Session
  // splash, so the collapsed flag can't just be a plain useState initializer —
  // it has to reset per agent. Tracking the agent id alongside it and adjusting
  // state during render (React's documented alternative to an effect for this)
  // re-syncs from localStorage exactly when the agent changes, with a single
  // read instead of one on every render.
  const [trackedAgentId, setTrackedAgentId] = useState(agent?.id ?? null);
  const [guideCollapsed, setGuideCollapsedState] = useState(() =>
    agent ? readGuideCardCollapsed(agent.id) : true
  );
  if (agent && agent.id !== trackedAgentId) {
    setTrackedAgentId(agent.id);
    setGuideCollapsedState(readGuideCardCollapsed(agent.id));
  }

  const setGuideCollapsed = (collapsed: boolean) => {
    if (agent) {
      writeGuideCardCollapsed(agent.id, collapsed);
    }
    setGuideCollapsedState(collapsed);
  };

  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center gap-8 overflow-y-auto px-8 pb-8",
        anchorTop ? "justify-start pt-8" : "justify-center pt-8"
      )}
    >
      {agent && (
        <GuideCard
          agent={agent}
          avatar={avatar}
          nameAction={nameAction}
          headerExtras={headerExtras}
          starterPrompts={starterPrompts}
          onPromptClick={onPromptClick}
          onOpenOverview={onOpenOverview}
          collapsed={guideCollapsed}
          onToggleCollapsed={setGuideCollapsed}
        />
      )}
      <div className="w-full max-w-2xl">{children}</div>
    </div>
  );
};
